import React, { useRef } from 'react';
import { RangePreset } from '../../model/datePicker/types';
import { MovableRangePreset } from '../../model/datePicker/rangeFromPreset';
import './sidebar.scss';
import { t } from '../../i18n';

const RANGE_ITEMS: { preset: MovableRangePreset, label: string }[] = [
  { preset: 'today', label: t('datePicker.rangePresets.today') },
  { preset: 'yesterday', label: t('datePicker.rangePresets.yesterday') },
  { preset: '1w', label: t('datePicker.rangePresets.1w') },
  { preset: '2w', label: t('datePicker.rangePresets.2w') },
  { preset: '1m', label: t('datePicker.rangePresets.1m') },
  { preset: '1y', label: t('datePicker.rangePresets.1y') }
];

// 7 items bajo "Range" — seleccionado en Gray 12 Medium, resto Gray 09
// Regular, sin píldora (§04). "Custom" es una consecuencia de hacer clic en
// el calendario (§08), no un disparador propio: se muestra pero no se pulsa,
// y por eso queda fuera del radiogroup navegable por teclado (§10).
const Sidebar = ({ selected, onSelect }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const move = (delta: number) => {
    const currentIndex = RANGE_ITEMS.findIndex(item => item.preset === selected);
    const fromIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (fromIndex + delta + RANGE_ITEMS.length) % RANGE_ITEMS.length;
    const next = RANGE_ITEMS[nextIndex];
    onSelect(next.preset);
    containerRef.current?.querySelector<HTMLButtonElement>(`[data-preset="${next.preset}"]`)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if(event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(1);
    }
    else if(event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      move(-1);
    }
  };

  return (
    <div className="rdp-sidebar" role="radiogroup" aria-label={t('datePicker.range')} ref={containerRef} onKeyDown={onKeyDown}>
      <div className="rdp-sidebar__label">{t('datePicker.range')}</div>
      {
        RANGE_ITEMS.map(item => {
          const checked = selected === item.preset;
          // Si la selección real es 'custom' (fuera de este grupo), el
          // primer item queda como ancla tabulable — nunca cero tabbables.
          const tabbable = checked || (selected === 'custom' && item.preset === 'today');
          return (
            <button
              key={item.preset}
              type="button"
              role="radio"
              aria-checked={checked}
              data-preset={item.preset}
              tabIndex={tabbable ? 0 : -1}
              className={`rdp-sidebar__item ${checked ? 'rdp-sidebar__item--selected' : ''}`}
              onClick={() => onSelect(item.preset)}
            >
              {item.label}
            </button>
          );
        })
      }
      <span
        role="radio"
        aria-checked={selected === 'custom'}
        tabIndex={-1}
        className={`rdp-sidebar__item rdp-sidebar__item--static ${selected === 'custom' ? 'rdp-sidebar__item--selected' : ''}`}
      >
        {t('datePicker.rangePresets.custom')}
      </span>
    </div>
  );
}

interface Props {
  selected: RangePreset,
  onSelect: (preset: MovableRangePreset) => void
}

export { Sidebar };
